import React, { useState, useCallback, useEffect } from "react";
import KioskLogin from "./KioskLogin";
import KioskProgramCards from "./KioskProgramCards";
import KioskSlotCards from "./KioskSlotCards";
import KioskAthleteCards from "./KioskAthleteCards";
import KioskAthletePinEntry from "./KioskAthletePinEntry";
import KioskStaffRoster from "./KioskStaffRoster";
import {
  clearKioskSession,
  KIOSK_SESSION_ENDED_EVENT,
  KioskProgram,
  KioskSlot,
  KioskStaffDay,
  getTenantSettings,
  getKioskModeConfig,
  getStaffPrograms,
} from "../../api/kioskAPI";
import "./KioskMode.css";
import "./KioskStaffRoster.css";
import { captureError, captureKioskError } from "../../utils/sentry";
import { safeGetItem } from "@mediatechgroup/app-version";

type KioskScreen =
  | "login"
  | "programs"
  | "slots"
  | "athletes"
  | "athlete-pin-entry"
  // Staff kiosk mode (kiosk_config.staff_mode_enabled). When the tenant turned
  // it on, the unlocked kiosk first asks which mode to run: staff attendance
  // (whole class rosters, today/yesterday) or the athlete check-in it always
  // had. With the setting off, none of these screens is reachable.
  | "mode-choice"
  | "staff-programs"
  | "staff-slots"
  | "staff-roster";

interface KioskModeProps {
  onExitKiosk?: () => void;
}

const DEFAULT_PRIMARY_COLOR = "#4b5563";
const DEFAULT_TIMEZONE = "America/New_York";

export interface KioskSettings {
  primaryColor: string;
  logoUrl?: string;
  timezone: string;
  // Defaults true (preserves GoY's makeup-class behavior). When the
  // tenant disables `settings.allow_makeups`, the kiosk hides the
  // Make Up card and the pin-entry's no-enrollment fallback instead
  // shows a "no class today" error.
  allowMakeups: boolean;
}

const KioskMode: React.FC<KioskModeProps> = ({ onExitKiosk }) => {
  const [currentScreen, setCurrentScreen] = useState<KioskScreen>("login");
  const [selectedProgram, setSelectedProgram] = useState<KioskProgram | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<KioskSlot | null>(null);
  const [requireAthletePin, setRequireAthletePin] = useState<boolean>(false);
  const [staffDay, setStaffDay] = useState<KioskStaffDay>("today");
  // The tenant calendar day the backend listed the staff classes for.
  const [staffDate, setStaffDate] = useState<string | null>(null);
  const [athletePinLength, setAthletePinLength] = useState<number>(4);
  const [kioskSettings, setKioskSettings] = useState<KioskSettings>({
    primaryColor: DEFAULT_PRIMARY_COLOR,
    timezone: DEFAULT_TIMEZONE,
    allowMakeups: true,
  });

  // A new kiosk PIN (or removing this device) ends the session on the
  // server; go back to the PIN screen.
  useEffect(() => {
    const onEnded = () => {
      setSelectedProgram(null);
      setSelectedSlot(null);
      setRequireAthletePin(false);
      setCurrentScreen("login");
    };
    window.addEventListener(KIOSK_SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(KIOSK_SESSION_ENDED_EVENT, onEnded);
  }, []);

  // Load tenant settings - try API first, fallback to localStorage
  useEffect(() => {
    const loadSettings = async () => {
      // First, try to fetch from API (works even when not logged in)
      const apiSettings = await getTenantSettings();
      if (apiSettings) {
        setKioskSettings({
          primaryColor: apiSettings.primary_color || DEFAULT_PRIMARY_COLOR,
          logoUrl: apiSettings.logo_url || undefined,
          timezone: apiSettings.timezone || DEFAULT_TIMEZONE,
          allowMakeups: apiSettings.allow_makeups !== false,
        });
        return;
      }

      // Fallback to localStorage (for offline/cached scenarios)
      const storedSettings = safeGetItem("settings");
      if (storedSettings) {
        try {
          const settings = JSON.parse(storedSettings);
          setKioskSettings({
            primaryColor: settings?.primary_color || DEFAULT_PRIMARY_COLOR,
            logoUrl: settings?.logo_url || undefined,
            timezone: settings?.timezone || DEFAULT_TIMEZONE,
            allowMakeups: settings?.allow_makeups !== false,
          });
        } catch (e) {
          captureError(e, { component: "KioskMode", operation: "parseSettings" }, "warning");
        }
      }
    };

    loadSettings();
  }, []);

  const handleLoginSuccess = useCallback(async () => {
    // Check kiosk mode configuration
    try {
      const modeConfig = await getKioskModeConfig();
      if (modeConfig.success) {
        const pinMode = !!modeConfig.data.requireAthletePin;
        setRequireAthletePin(pinMode);
        setAthletePinLength(modeConfig.data.pinLength || 4);
        // Staff mode on: whoever unlocked the kiosk picks the mode. Only a
        // literal true counts; anything else keeps the self check-in flow.
        if (modeConfig.data.staffModeEnabled === true) {
          setCurrentScreen("mode-choice");
          return;
        }
        if (pinMode) {
          setCurrentScreen("athlete-pin-entry");
          return;
        }
      }
    } catch (err) {
      captureKioskError(err, "fetchKioskModeConfig");
    }
    // Default to staff selection mode
    setCurrentScreen("programs");
  }, []);

  const handleChooseSelfCheckIn = useCallback(() => {
    setCurrentScreen(requireAthletePin ? "athlete-pin-entry" : "programs");
  }, [requireAthletePin]);

  const handleChooseStaff = useCallback(() => {
    setStaffDay("today");
    setSelectedProgram(null);
    setSelectedSlot(null);
    setCurrentScreen("staff-programs");
  }, []);

  const loadStaffPrograms = useCallback(async () => {
    const response = await getStaffPrograms(staffDay);
    if (response?.success) setStaffDate(response.meta.date);
    return response;
  }, [staffDay]);

  const handleSelectStaffProgram = useCallback((program: KioskProgram) => {
    setSelectedProgram(program);
    if (program.slots.length === 1) {
      setSelectedSlot(program.slots[0]);
      setCurrentScreen("staff-roster");
    } else {
      setCurrentScreen("staff-slots");
    }
  }, []);

  const handleSelectStaffSlot = useCallback((slot: KioskSlot) => {
    setSelectedSlot(slot);
    setCurrentScreen("staff-roster");
  }, []);

  const handleBackToStaffPrograms = useCallback(() => {
    setSelectedProgram(null);
    setSelectedSlot(null);
    setCurrentScreen("staff-programs");
  }, []);

  const handleBackToStaffSlots = useCallback(() => {
    setSelectedSlot(null);
    setCurrentScreen("staff-slots");
  }, []);

  const handleSelectProgram = useCallback((program: KioskProgram) => {
    setSelectedProgram(program);
    // If program has only one slot, skip slot selection
    if (program.slots.length === 1) {
      setSelectedSlot(program.slots[0]);
      setCurrentScreen("athletes");
    } else {
      setCurrentScreen("slots");
    }
  }, []);

  const handleSelectSlot = useCallback((slot: KioskSlot) => {
    setSelectedSlot(slot);
    setCurrentScreen("athletes");
  }, []);

  const handleBackToPrograms = useCallback(() => {
    setSelectedProgram(null);
    setSelectedSlot(null);
    setCurrentScreen("programs");
  }, []);

  const handleBackToSlots = useCallback(() => {
    setSelectedSlot(null);
    setCurrentScreen("slots");
  }, []);

  const handleLogout = useCallback(() => {
    clearKioskSession();
    setSelectedProgram(null);
    setSelectedSlot(null);
    setRequireAthletePin(false);
    setCurrentScreen("login");
    if (onExitKiosk) {
      onExitKiosk();
    }
  }, [onExitKiosk]);

  const handleExitAthletePinMode = useCallback(() => {
    clearKioskSession();
    setRequireAthletePin(false);
    setCurrentScreen("login");
    if (onExitKiosk) {
      onExitKiosk();
    }
  }, [onExitKiosk]);

  const renderScreen = () => {
    switch (currentScreen) {
      case "login":
        return <KioskLogin onLoginSuccess={handleLoginSuccess} />;

      case "athlete-pin-entry":
        return (
          <KioskAthletePinEntry
            onExit={handleExitAthletePinMode}
            pinLength={athletePinLength}
            allowMakeups={kioskSettings.allowMakeups}
          />
        );

      case "programs":
        return (
          <KioskProgramCards
            onSelectProgram={handleSelectProgram}
            onLogout={handleLogout}
            settings={kioskSettings}
          />
        );

      case "slots":
        return selectedProgram ? (
          <KioskSlotCards
            program={selectedProgram}
            onSelectSlot={handleSelectSlot}
            onBack={handleBackToPrograms}
            settings={kioskSettings}
          />
        ) : null;

      case "athletes":
        return selectedProgram && selectedSlot ? (
          <KioskAthleteCards
            program={selectedProgram}
            slot={selectedSlot}
            onBack={selectedProgram.slots.length === 1 ? handleBackToPrograms : handleBackToSlots}
            onAttendanceComplete={handleBackToPrograms}
            settings={kioskSettings}
          />
        ) : null;

      case "mode-choice":
        return (
          <div className="kiosk-screen kiosk-mode-choice-screen">
            <div className="kiosk-header">
              <div className="kiosk-header__title">
                <h1>How will this kiosk be used?</h1>
                <p>Choose a mode. To switch later, use Staff Logout and the club PIN.</p>
              </div>
            </div>
            <div className="kiosk-mode-choice">
              <button className="kiosk-mode-choice__card" onClick={handleChooseStaff}>
                <h2>Staff attendance</h2>
                <p>Mark whole classes Present or Absent for today or yesterday. Marks are recorded as “Kiosk”.</p>
              </button>
              <button className="kiosk-mode-choice__card" onClick={handleChooseSelfCheckIn}>
                <h2>Athlete check-in</h2>
                <p>Athletes check themselves in for today’s classes.</p>
              </button>
            </div>
          </div>
        );

      case "staff-programs":
        return (
          <KioskProgramCards
            key={staffDay}
            loadPrograms={loadStaffPrograms}
            title="Staff attendance — choose a class"
            emptyText={staffDay === "yesterday" ? "No classes met yesterday" : "No classes scheduled for today"}
            headerExtra={
              <div>
                <div className="kiosk-day-switch" role="group" aria-label="Attendance day">
                  {(["today", "yesterday"] as KioskStaffDay[]).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className="kiosk-day-switch__btn"
                      aria-pressed={staffDay === d}
                      onClick={() => setStaffDay(d)}
                    >
                      {d === "today" ? "Today" : "Yesterday"}
                    </button>
                  ))}
                </div>
                <div>
                  <button type="button" className="kiosk-mode-link" onClick={() => setCurrentScreen("mode-choice")}>
                    Change mode
                  </button>
                </div>
              </div>
            }
            onSelectProgram={handleSelectStaffProgram}
            onLogout={handleLogout}
            settings={kioskSettings}
          />
        );

      case "staff-slots":
        return selectedProgram ? (
          <KioskSlotCards
            program={selectedProgram}
            onSelectSlot={handleSelectStaffSlot}
            onBack={handleBackToStaffPrograms}
            settings={kioskSettings}
          />
        ) : null;

      case "staff-roster":
        return selectedProgram && selectedSlot && staffDate ? (
          <KioskStaffRoster
            program={selectedProgram}
            slot={selectedSlot}
            date={staffDate}
            day={staffDay}
            onBack={selectedProgram.slots.length === 1 ? handleBackToStaffPrograms : handleBackToStaffSlots}
          />
        ) : null;

      default:
        return null;
    }
  };

  return (
    <div
      className="kiosk-mode"
      style={{ "--kiosk-primary": kioskSettings.primaryColor } as React.CSSProperties}
    >
      {renderScreen()}
    </div>
  );
};

export default KioskMode;
